/* eslint-disable max-len */
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Flex, Text, Toast, Label, Pane, TextInput, Button } from '@leverege/ui-elements';
import { Auth } from '@leverege/api-redux';
import I18N from '@leverege/i18n';
import { Validator } from '@leverege/validator';
import { PasswordValidatorFactory } from '@leverege/api-validator';
import { useForm, FieldErrorsComponent } from '@leverege/api-ui-login';
import { useMolten } from '@leverege/molten-core';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = I18N.ns('molten.screens.login.ChangePasswordForm');
function ChangePasswordForm(props) {
  const {
    dispatch,
    onSubmit: pOnSubmit
  } = props;
  const [passwordValidator, setPasswordValidator] = useState();
  const [passwordErrors, setPasswordErrors] = useState();
  const molten = useMolten();
  useEffect(() => {
    const api = molten.get('api');
    const prjId = api.options.projectId;
    api.project(prjId).passwordRequirements().get().then(reqs => {
      setPasswordValidator(PasswordValidatorFactory.create(reqs));
    });
  }, []);
  const submit = useCallback(async data => {
    const {
      current,
      new1
    } = data;
    try {
      await dispatch(Auth.changePassword(current, new1));
      Toast.success(t('successPasswordChange'));
      pOnSubmit();
    } catch (ex) {
      if (ex.resultCodes) {
        setPasswordErrors(Object.values(ex.resultCodes));
      }
      Toast.error(t('errorPasswordChange'));
    }
  }, [dispatch, pOnSubmit]);
  const pOnChange = useCallback(() => {
    setPasswordErrors(null);
  }, []);
  const {
    errors,
    values,
    onChange,
    onSubmit
  } = useForm({
    fields: {
      current: {
        validator: useCallback((value, state) => {
          const valid = Validator.required(value);
          return valid || 'Current password is required';
        }, [])
      },
      new1: {
        validator: useCallback((value, state) => {
          if (!passwordValidator) {
            return true;
          }
          const validationResult = passwordValidator.validate(value);
          return validationResult.valid ? true : Object.values(validationResult.errors);
        }, [passwordValidator])
      },
      new2: {
        validator: useCallback((value, state) => {
          if (!value) {
            return 'Password confirmation is required';
          }
          if (value !== state.new1) {
            return 'Password confirmation does not match password';
          }
          return true;
        }, [])
      }
    },
    onSubmit: submit,
    onChange: pOnChange
  });
  return /*#__PURE__*/_jsxs(Flex, {
    variant: "colL",
    children: [/*#__PURE__*/_jsx(Flex, {
      variant: "colL",
      children: /*#__PURE__*/_jsxs(Flex, {
        variant: "setting|colM",
        children: [/*#__PURE__*/_jsx(Label, {
          variant: "inputTitle|title",
          children: t('currentPassword')
        }), /*#__PURE__*/_jsx(TextInput, {
          autoComplete: "off",
          variant: "password|inputGrow",
          hint: t('currentPasswordHint'),
          type: "password",
          eventData: "current",
          onChange: onChange,
          value: values.current
        }), /*#__PURE__*/_jsx(FieldErrorsComponent, {
          errors: errors.current
        })]
      })
    }), /*#__PURE__*/_jsx(Flex, {
      variant: "colL",
      children: /*#__PURE__*/_jsxs(Flex, {
        variant: "setting|colM",
        children: [/*#__PURE__*/_jsx(Label, {
          variant: "inputTitle|title",
          children: t('newPassword')
        }), /*#__PURE__*/_jsx(TextInput, {
          autoComplete: "off",
          variant: "password|inputGrow",
          hint: t('newPasswordHint'),
          type: "password",
          eventData: "new1",
          onChange: onChange,
          value: values.new1,
          changeOnReturn: false
        }), /*#__PURE__*/_jsx(PasswordErrors, {
          value: values.new1,
          validator: passwordValidator,
          errors: passwordErrors || errors.new1
        }), /*#__PURE__*/_jsx(TextInput, {
          autoComplete: "off",
          variant: "password|inputGrow",
          hint: t('newPasswordHintReenter'),
          type: "password",
          eventData: "new2",
          onChange: onChange,
          value: values.nwe2,
          changeOnReturn: false
        }), /*#__PURE__*/_jsx(FieldErrorsComponent, {
          errors: errors.new2
        })]
      })
    }), /*#__PURE__*/_jsx(Flex, {
      variant: "colL",
      children: /*#__PURE__*/_jsx(Flex, {
        justify: "space-between",
        children: /*#__PURE__*/_jsx(Button, {
          variant: "settings",
          onClick: onSubmit,
          children: t('saveNewPassword')
        })
      })
    })]
  });
}
function PasswordErrors(props) {
  const {
    value,
    errors,
    validator
  } = props;
  const requirements = validator?.getRequirements();
  if (!requirements) {
    return null;
  }
  const e = Object.values(requirements).map(requirement => {
    const {
      type: code,
      ...rest
    } = requirement;
    if (errors != null) {
      const error = errors.find(err => err.type === code);
      const isError = !error?.valid;
      return /*#__PURE__*/_jsx(Text, {
        variant: isError ? 'passwordRequirementError' : 'passwordRequirementSuccess',
        children: I18N.t(code, rest)
      }, code);
    }
    if (!value) {
      return /*#__PURE__*/_jsx(Text, {
        variant: "passwordRequirement",
        children: I18N.t(code, rest)
      }, code);
    }
    return /*#__PURE__*/_jsx(Text, {
      variant: "passwordRequirementSuccess",
      children: I18N.t(code, rest)
    }, code);
  });
  return /*#__PURE__*/_jsx(Pane, {
    variant: "passwordRequirements",
    layout: "flex:colM",
    children: e
  });
}
export default connect(state => {
  return {
    auth: state.auth,
    location: state.router.location.pathname
  };
})(ChangePasswordForm);